/**
 * Define la tabla de personas y sus reglas compartidas por el servidor y el panel. El
 * correo se almacena en la cuenta de la aplicación y los roles en app_access; la
 * colección de datos guarda el enlace member y las columnas propias.
 *
 * Estas funciones permiten crear y proteger las columnas del sistema, mostrar las
 * personas y enviar cada cambio al lugar donde se almacena.
 */
import {
  type AppPerson,
  type FieldDef,
  identifier,
  isRelationField,
  type TableRecord,
} from "./types.ts";

/** Nombre técnico de la tabla de personas dentro de cada aplicación. */
export const PEOPLE_TABLE = "personas";

/**
 * Como se lee la tabla de personas en la lista de tablas.
 *
 * Dice "y roles" porque los roles se gestionan desde ella: no hay otra pantalla
 * donde se creen. El nombre no lo cambia el constructor --la tabla de usuarios
 * no se renombra-- así que se repone en cada arranque.
 */
export const PEOPLE_TABLE_LABEL = "Personas y roles";

/**
 * Lo mínimo que puede medir la clave de una persona invitada.
 *
 * Lo dicen tres sitios --el panel al escribirla, el servidor al recibirla y la
 * colección de cuentas al guardarla-- y los tres leen este número. El tercero
 * no se entera solo: se le pone al día al arrancar. Ver `ensurePasswordMin` en
 * `server/bootstrap.ts`.
 */
export const MIN_PASSWORD = 6;

/**
 * Una clave que nadie tenga que inventar: larga y de una sola vez.
 *
 * Sale de aquí y no de cada sitio que la necesita --invitar, importar, migrar--
 * porque las tres tienen que dar claves igual de buenas, y tres copias del
 * alfabeto son tres sitios donde una puede quedarse corta sin que se note.
 */
export function newPassword(): string {
  const abc = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  // El resto de la división no reparte por igual: con 56 letras y 256 valores,
  // las 32 primeras salen una vez más que las demás. Se descartan los valores
  // que caen en ese sobrante y se piden otros, que es lo que deja las 56
  // igual de probables.
  const limit = 256 - (256 % abc.length);
  let out = "";
  while (out.length < 12) {
    for (const n of crypto.getRandomValues(new Uint8Array(12))) {
      if (n >= limit) continue;
      out += abc[n % abc.length];
      if (out.length === 12) break;
    }
  }
  return out;
}

/**
 * La identidad con la que una persona entra en una aplicación.
 *
 * No es el correo a secas y no puede serlo: el mismo correo puede tener cuenta
 * en varias aplicaciones, cada una con su clave, y PocketBase necesita saber
 * cual de ellas se está abriendo antes de comparar nada. Por eso la identidad
 * lleva delante la aplicación.
 *
 * Quien entra no la escribe ni la ve: el formulario sigue pidiendo el correo y
 * es la pantalla la que le pone delante la aplicación en la que está. Ver
 * `passwordAuth.identityFields` en `server/bootstrap.ts`.
 */
export function loginFor(appId: string, cuenta: string): string {
  const email = String(cuenta ?? "")
    .trim()
    .toLowerCase();
  return appId && email ? `${appId}:${email}` : "";
}

/**
 * Columna real que enlaza la fila con la cuenta.
 *
 * No es del constructor y no aparece como columna: es la identidad de la fila.
 * Por eso `member` está entre los nombres reservados en `server/schema.ts`.
 */
export const MEMBER_FIELD = "member";

/**
 * Nombre técnico de la columna del correo, la llave que toda persona tiene.
 *
 * Es la que una columna que apunta a personas enseña por defecto, y la única
 * que existe desde el primer día: las demás --un documento, un código-- llegan
 * cuando la aplicación las añade.
 */
export const PEOPLE_ACCOUNT_FIELD = "cuenta";

/** Como se lee esa columna cuando hay que nombrarla en un aviso. */
const PEOPLE_ACCOUNT_LABEL = "Correo";

/**
 * Las dos columnas del sistema de la tabla de personas.
 *
 * Se ven y se editan en la cuadrícula como cualquier otra, pero no se borran,
 * no se renombran y no cambian de tipo: son las que sostienen el acceso. Si la
 * cuadrícula no las enseñara habría que mantener a su lado la pantalla vieja
 * para tocarlas, que es justo lo que este cambio viene a quitar.
 *
 * No hay una tercera que diga lo que una persona puede hacer con los datos: lo
 * que puede hacer ya no se declara por persona. Guardar exige cuenta iniciada y
 * nada más; los roles deciden que pantallas ve.
 *
 * `cuenta` es única dentro de la aplicación --dos invitados suyos no comparten
 * correo-- y por eso sirve de llave desde el primer día, antes de que nadie
 * añada un documento. Fuera de ella no dice nada: el mismo correo puede estar
 * invitado en otra aplicación, y allí es otra persona con otra clave.
 */
export const PEOPLE_SYSTEM_FIELDS: FieldDef[] = [
  {
    name: PEOPLE_ACCOUNT_FIELD,
    label: PEOPLE_ACCOUNT_LABEL,
    type: "email",
    required: true,
    unique: true,
    system: "cuenta",
  },
  {
    name: "roles",
    label: "Roles",
    type: "select",
    multiple: true,
    system: "roles",
  },
];

/**
 * Nombre técnico de la columna que dice como se llama una persona.
 *
 * Lo preguntan sitios que no se ven a la vez --quien la repone al arrancar,
 * quien la defiende al guardar columnas, la tarjeta que las edita y
 * `personDisplayName`-- y una cadena suelta en cada uno es una forma de que
 * uno de ellos deje de hablar de la misma columna.
 */
export const PEOPLE_NAME_FIELD = "nombre";

/** Como se lee esa columna cuando hay que nombrarla en un aviso. */
export const PEOPLE_NAME_LABEL = "Nombre";

/**
 * La columna con la que nace la tabla de personas, además de las dos de arriba.
 *
 * Un correo no dice quien es nadie: `1037660432@ss.local` es una fila que hay
 * que descifrar. Por eso está el primer día y por eso no se puede borrar: es
 * de donde sale el nombre de una persona en toda la plataforma --el saludo de
 * una página publicada, la celda de una columna de persona, el desplegable que
 * la elige-- y sin ella no queda más que el correo.
 *
 * No lleva marca de sistema porque su valor si vive en la colección de la
 * tabla, que es lo que esa marca decide (ver `storedFields`). En la cuadrícula
 * se trata como la del correo: se ordena y se esconde, y su menú no ofrece
 * editarla. Lo que se rellena en cada fila es de la aplicación, como siempre.
 */
export const PEOPLE_DEFAULT_FIELDS: FieldDef[] = [
  { name: PEOPLE_NAME_FIELD, label: PEOPLE_NAME_LABEL, type: "text" },
];

/** Las columnas con las que nace la tabla de personas, en su orden. */
export function peopleInitialFields(): FieldDef[] {
  return [...structuredClone(PEOPLE_SYSTEM_FIELDS), ...structuredClone(PEOPLE_DEFAULT_FIELDS)];
}

/**
 * Nombres que una columna propia de la tabla de personas no puede llevar, y
 * que ocupa cada uno.
 *
 * Una página publicada recibe a quien la mira en un solo nivel
 * --`plane.usuario.cedula`, no `plane.usuario.campos.cedula`-- porque es lo que
 * se escribe en el filtro de una pantalla de "lo mío". El precio de lo plano es
 * que una columna llamada como un dato de la plataforma lo taparía. Se paga
 * aquí: rechazar el nombre una vez es más claro que resolver el choque cada vez
 * que se arma la sesión. Ver `design.md` D4 de
 * `relacion-automatica-con-personas`.
 *
 * `nombre` no está en la lista. También viaja en la sesión, pero no hay choque
 * que evitar: la columna que la tabla trae desde el primer día es de donde sale
 * ese mismo dato (`PEOPLE_DEFAULT_FIELDS`, `personDisplayName`), así que
 * prohibirla rechazaría la tabla de toda aplicación que ya existe.
 */
export const PEOPLE_RESERVED_NAMES: Record<string, string> = {
  id: "el identificador de la persona",
  correo: "el correo con el que entra",
  roles: "los roles que tiene en la aplicación",
};

/**
 * Que ocupa ese nombre en la tabla de personas, o vacío si se puede usar.
 *
 * Se compara el nombre técnico y no lo que se escribió: "Correo" y "correo"
 * acaban siendo la misma columna. Fuera de la tabla de personas no hay nada
 * que tapar y siempre se puede.
 */
export function reservedPersonName(
  table: Pick<TableRecord, "system" | "name"> | null,
  name: string,
): string {
  if (!isPeopleTable(table)) return "";
  return PEOPLE_RESERVED_NAMES[identifier(name)] ?? "";
}

/**
 * El icono con el que se reconoce un rol en toda la plataforma.
 *
 * Los roles se nombran en sitios que no se ven a la vez --la columna de la
 * tabla de personas, la tarjeta que los crea, las opciones de cada página, la
 * vista previa-- y sin una marca común cada uno parecía una cosa distinta. Es
 * un nombre de la fuente de iconos (ver `shared/icons.ts`), así que se pasa tal
 * cual a `Icon`.
 */
export const ROLE_ICON = "user-account";

/**
 * Cuantos roles puede tener una aplicación y cuanto mide el nombre de uno.
 *
 * El tope existe porque los roles se pintan como opciones de una celda y como
 * renglones marcables en las opciones de cada página: una lista sin fondo no se
 * puede repasar, y quien la mira ya no sabe a quien está abriendo la pantalla.
 */
export const MAX_ROLES = 24;
const MAX_ROLE_LENGTH = 40;

/**
 * El rol que toda aplicación define desde que nace, sin que nadie lo escriba.
 *
 * Es el de quien construye cuando mira sus propias páginas, y es donde arranca
 * la vista previa. No se puede quitar desde la pantalla de roles: quitarlo
 * dejaría a `pruneRoles` borrándolo de las páginas que lo tuvieran marcado.
 *
 * Dárselo a una persona invitada no le da nada del panel: el poder de quien
 * construye viene de ser dueño de la aplicación, no del nombre del rol. Ver
 * `design.md` D7.
 */
export const ADMIN_ROLE = "admin";

/**
 * El nombre de un rol, en la forma en que se guarda.
 *
 * Minúsculas, sin tildes ni signos, los espacios convertidos en guiones y los
 * guiones repetidos colapsados. Se aplica en los tres sitios por los que entra
 * un rol --el campo donde se escribe, el saneado del servidor y la importación
 * de personas-- y en el campo se aplica en cada pulsación, para que quien lo
 * escribe vea el nombre tal como va a quedar.
 *
 * Se guarda una sola forma y no la pareja "escrito / normalizado": el HTML de
 * una página compara contra el nombre guardado, y tener dos formas obligaría a
 * saber cual se usa en cada sitio. Ver `design.md` D6.
 */
export function normalizeRole(name: unknown): string {
  // El guion del final se quita después de cortar por el tope: cortar puede
  // dejar uno donde no lo había.
  return roleDraft(name).replace(/-+$/g, "");
}

/**
 * El nombre de un rol mientras se escribe.
 *
 * Es `normalizeRole` sin quitar el guion del final. Hace falta para el campo:
 * quien escribe "jefe de zona" pasa por "jefe-" en cuanto pulsa el espacio, y
 * recortarlo en ese momento le impediria escribir la segunda palabra.
 *
 * Lo que se guarda es siempre `normalizeRole`, aquí y en el servidor.
 */
export function roleDraft(name: unknown): string {
  return (
    String(name ?? "")
      .normalize("NFD")
      // Las marcas diacriticas que NFD separo de su letra. La ene queda en `n`.
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+/g, "")
      .slice(0, MAX_ROLE_LENGTH)
  );
}

/**
 * Normaliza una lista de roles conservando el orden y fundiendo los que
 * colisionen. Lo que se normaliza a vacío se descarta.
 */
export function normalizeRoles(names: unknown): string[] {
  const list = Array.isArray(names) ? names : [];
  const out: string[] = [];
  for (const raw of list) {
    const role = normalizeRole(raw);
    if (role && !out.includes(role)) out.push(role);
  }
  return out;
}

/** Una columna que sostiene la plataforma: ni se borra, ni se renombra, ni cambia de tipo. */
function isSystemField(field: Pick<FieldDef, "system">): boolean {
  return field.system !== undefined;
}

/** Las columnas que si viven en la colección de la tabla. */
export function storedFields(fields: FieldDef[]): FieldDef[] {
  return fields.filter((f) => !isSystemField(f));
}

/**
 * Si una columna de la tabla de personas hay que superponerla al pintar.
 *
 * Es la única asimetria que le queda a esta tabla frente a las demás: dos de
 * sus columnas --el correo y los roles-- no están en su colección, así que una
 * relación que apunte a ella no las trae con el expand de la base ni las puede
 * buscar con un filtro corriente. Quien las tiene es la lista de invitados.
 *
 * Se pregunta por la tabla y no solo por el nombre: "cuenta" es una columna
 * cualquiera en cualquier otra tabla, y allí vive en su colección como todas.
 */
export function isOverlayField(
  table: Pick<TableRecord, "system" | "name" | "fields"> | null,
  name: string,
): boolean {
  if (!isPeopleTable(table)) return false;
  const field = (table?.fields ?? []).find((f) => f.name === name);
  return field !== undefined && isSystemField(field);
}

/** La tabla de personas de una aplicación, si ya existe. */
export function peopleTableOf(tables: TableRecord[]): TableRecord | null {
  return tables.find((t) => t.system === true && t.name === PEOPLE_TABLE) ?? null;
}

/** Si esta tabla es la de personas. */
export function isPeopleTable(table: Pick<TableRecord, "system" | "name"> | null): boolean {
  return table?.system === true && table.name === PEOPLE_TABLE;
}

/**
 * Si esta columna es la del nombre de la tabla de personas: la que no se borra.
 *
 * Se pregunta por la tabla y no solo por el nombre porque "nombre" es una
 * columna cualquiera en cualquier otra tabla, y allí se borra como todas.
 * Ver `PEOPLE_DEFAULT_FIELDS`.
 */
export function isPeopleNameField(
  table: Pick<TableRecord, "system" | "name"> | null,
  field: Pick<FieldDef, "name">,
): boolean {
  return isPeopleTable(table) && field.name === PEOPLE_NAME_FIELD;
}

/**
 * Los roles de la aplicación, como opciones de la columna de roles.
 *
 * Se rellenan al leer y no al guardar: los roles se añaden y se quitan desde la
 * tarjeta de roles, y una copia guardada en la tabla se quedaría vieja en cuanto
 * alguien tocara aquello. Quien las rellena es `Builder` al traer las tablas,
 * así que todo el que las lea --la cuadrícula, exportar la estructura, la
 * tarjeta de una columna-- las ve ya puestas.
 */
export function withRoleOptions(fields: FieldDef[], roles: string[]): FieldDef[] {
  return fields.map((f) => (f.system === "roles" ? { ...f, options: [...roles] } : f));
}

/**
 * El aviso de que quitar a alguien se lleva por delante su cuenta aquí.
 *
 * Se dice antes de confirmar porque no tiene vuelta atrás. Nombra la cuenta
 * --que es de esta aplicación, y borrarla es dejar a esa persona sin entrada--
 * y nombra también lo que no se toca: si está invitada en otra aplicación,
 * allí tiene su propia cuenta y esta no la roza.
 */
export function removeWarning(label: string, ownColumns: number): string {
  const who = label || "esta persona";
  const resto = "Si está invitada en otra aplicación, allí no cambia nada.";
  if (ownColumns === 0) {
    return `${who} pierde su cuenta en esta aplicación y deja de poder entrar. ${resto}`;
  }
  const columnas = ownColumns === 1 ? "columna propia" : "columnas propias";
  return `${who} pierde su cuenta en esta aplicación y deja de poder entrar, y se pierde lo que esta aplicación sabía de ella: ${ownColumns} ${columnas}. No tiene vuelta atrás. ${resto}`;
}

/**
 * Las columnas de la tabla de personas por las que una columna de persona de
 * otra tabla puede mostrar y emparejar.
 *
 * Son todas las que pueden nombrar a alguien, y no solo las declaradas sin
 * repetidos. La marca de "sin repetidos" la pone el constructor a mano, y la
 * columna con la que de verdad se reconoce a la gente --"cédula", "documento",
 * "código"-- casi siempre llega importando la nómina y nace como texto
 * corriente, sin marca. Exigirla dejaba fuera justo esa, que es la que todo el
 * mundo usa.
 *
 * Que un valor identifique o no se decide mirando el dato y no la marca: el que
 * señala a dos personas no enlaza a ninguna. Ver `guessPersonColumns` en
 * `web/src/lib/personGuess.ts` y `matchValues` en `shared/relations.ts`.
 *
 * Sin la tabla de personas a mano --una aplicación muy vieja, a medio
 * arrancar-- se cae a la cuenta, que es la única que siempre existe.
 */
export function personKeyFields(tables: TableRecord[]): { name: string; label: string }[] {
  const people = peopleTableOf(tables);
  if (!people) return [{ name: "cuenta", label: "Correo" }];
  return people.fields
    .filter((f) => !isRelationField(f) && f.type !== "file" && f.multiple !== true)
    .map((f) => ({ name: f.name, label: f.label || f.name }));
}

/** Una lista de personas indexada por el id de su fila y por su cuenta, con su posición. */
interface PeopleIndex {
  byRow: Map<string, { person: AppPerson; at: number }>;
  byAccount: Map<string, { person: AppPerson; at: number }>;
}

/**
 * Los índices ya armados, por lista. Valen mientras la lista sea la misma: en
 * ningún sitio se modifica una en su sitio, siempre se reemplaza entera.
 */
const peopleIndexes = new WeakMap<readonly AppPerson[], PeopleIndex>();

/**
 * La persona a la que apunta una celda: la de esa fila de la tabla de
 * personas, o la de esa cuenta. Si las dos existen y son distintas, gana la
 * que va antes en la lista.
 *
 * Da lo mismo que recorrer la lista con `find`, pero sin recorrerla: se
 * pregunta una vez por cada celda de persona, y con miles de invitados y
 * cientos de filas eran millones de comparaciones por cada vez que se pintaba.
 */
export function findPerson(
  people: readonly AppPerson[],
  row: string,
  account?: string,
): AppPerson | undefined {
  if (!people.length) return undefined;
  let index = peopleIndexes.get(people);
  if (!index) {
    const byRow: PeopleIndex["byRow"] = new Map();
    const byAccount: PeopleIndex["byAccount"] = new Map();
    people.forEach((person, at) => {
      if (person.fila && !byRow.has(person.fila)) byRow.set(person.fila, { person, at });
      if (!byAccount.has(person.id)) byAccount.set(person.id, { person, at });
    });
    index = { byRow, byAccount };
    peopleIndexes.set(people, index);
  }
  const byRow = index.byRow.get(row);
  const byAccount = account ? index.byAccount.get(account) : undefined;
  if (byRow && byAccount) return byRow.at <= byAccount.at ? byRow.person : byAccount.person;
  return (byRow ?? byAccount)?.person;
}

/**
 * Obtiene el valor de una columna de la persona. cuenta y email se aceptan como
 * referencias al correo para mantener compatibilidad con definiciones antiguas.
 *
 * Las columnas propias se leen primero de campos, en especial nombre: el name de la
 * cuenta puede ser solo el correo sin dominio. Solo usa los datos de cuenta como respaldo
 * cuando esa columna no está disponible.
 */
export function personKeyValue(person: AppPerson, key: string): string {
  if (key === "cuenta" || key === "email") return person.email ?? "";
  if (person.campos && key in person.campos) return String(person.campos[key] ?? "");
  if (key === "name" || key === "nombre") return person.name ?? "";
  return "";
}

/**
 * Obtiene el nombre visible desde la columna nombre de la tabla de personas.
 * AppPerson.name puede contener el correo sin dominio y no representar el nombre real.
 *
 * Si la columna aún no se ha cargado o está vacía, utiliza los datos de cuenta como
 * respaldo.
 */
export function personDisplayName(person: AppPerson): string {
  // La columna vacía no debe tapar un nombre que la cuenta todavía conserva.
  return personKeyValue(person, "nombre") || person.name || person.email || "";
}
